import axios from "axios";
import { useEffect, useState } from "react";
import CategoryBar from "./CategoryBar";
import "./home.css";
import { useNavigate, useLocation } from "react-router-dom";
import Banner from "./Banner";
import PopularCategories from "./PopularCategories";

// =====================================================
// API URL
// =====================================================

const API_URL =
  window.location.hostname === "localhost"
    ? "http://localhost:5000"
    : "https://apple2.shopat1stop.com";

export default function Home() {
  const [products, setProducts] = useState([]);

  const navigate = useNavigate();
  const location = useLocation();

  // =====================================================
  // URL PARAMETERS
  // =====================================================

  const params = new URLSearchParams(location.search);

  const search = params.get("search");
  const category = params.get("category");

  // =====================================================
  // HOME SECTIONS
  // =====================================================

  const showHomeSections =
    (!category || category === "All") &&
    !search;

  // =====================================================
  // IMAGE URL
  // =====================================================

  const getImageUrl = (image) => {
    if (!image) {
      return "";
    }

    const cleanImage = String(image).trim();

    // If database already contains full URL
    if (
      cleanImage.startsWith("http://") ||
      cleanImage.startsWith("https://")
    ) {
      return cleanImage;
    }

    // Make sure image starts with /
    const imagePath = cleanImage.startsWith("/")
      ? cleanImage
      : `/${cleanImage}`;

    return `${API_URL}${imagePath}`;
  };

  // =====================================================
  // FETCH PRODUCTS
  // =====================================================

  useEffect(() => {
    let url = `${API_URL}/api/products`;

    const queryParams = [];

    if (category && category !== "All") {
      queryParams.push(
        `category=${encodeURIComponent(category)}`
      );
    }

    if (search) {
      queryParams.push(
        `search=${encodeURIComponent(search)}`
      );
    }

    if (queryParams.length > 0) {
      url += `?${queryParams.join("&")}`;
    }

    console.log("PRODUCT API:", url);

    axios
      .get(url)
      .then((res) => {
        console.log("PRODUCTS:", res.data);

        if (Array.isArray(res.data)) {
          setProducts(res.data);
        } else {
          setProducts([]);
        }
      })
      .catch((err) => {
        console.log("PRODUCT API ERROR:", err);
        setProducts([]);
      });
  }, [location.search, category, search]);

  // =====================================================
  // ADD TO CART
  // =====================================================

  const addToCart = async (id) => {
    try {
      const user_id =
        localStorage.getItem("user_id");

      if (!user_id) {
        alert("Login first ❌");
        return;
      }

      const storeName =
        localStorage.getItem("storeLocation") ||
        "Apple 1";

      // KEEP THIS EXACTLY LIKE YOUR WORKING VERSION
      const res = await axios.post(
        "https://apple2.shopat1stop.com/api/cart",
        {
          user_id: parseInt(user_id),
          product_id: parseInt(id),
          store_name: storeName
        }
      );

      console.log("CART RESPONSE:", res.data);

      // Update Navbar cart count
      window.dispatchEvent(
        new Event("cartUpdated")
      );

      alert(
        res.data.message ||
        "Added to cart ✅"
      );

    } catch (err) {
      console.log("CART ERROR:", err);

      if (err.response) {
        console.log(
          "CART SERVER ERROR:",
          err.response.data
        );

        alert(
          err.response.data.message ||
          "Cart failed ❌"
        );
      } else {
        alert("Cart failed ❌");
      }
    }
  };

  // =====================================================
  // ADD TO WISHLIST
  // =====================================================

  const addToWishlist = async (id) => {
    try {
      const user_id =
        localStorage.getItem("user_id");

      if (!user_id) {
        alert("Login first ❌");
        return;
      }

      // KEEP THIS EXACTLY LIKE YOUR WORKING VERSION
      const res = await axios.post(
        "https://apple2.shopat1stop.com/api/wishlist",
        {
          user_id: parseInt(user_id),
          product_id: parseInt(id),
          location:
            localStorage.getItem("storeLocation")
        }
      );

      console.log(
        "WISHLIST RESPONSE:",
        res.data
      );

      alert(
        res.data.message ||
        "Added to wishlist ❤️"
      );

    } catch (err) {
      console.log(
        "WISHLIST ERROR:",
        err
      );

      if (err.response) {
        console.log(
          "WISHLIST SERVER ERROR:",
          err.response.data
        );

        alert(
          err.response.data.message ||
          "Wishlist failed ❌"
        );
      } else {
        alert("Wishlist failed ❌");
      }
    }
  };

  // =====================================================
  // PAGE
  // =====================================================

  return (
    <div>

      {/* =================================================
          CATEGORY BAR
      ================================================= */}

      <div className="category-wrapper">
        <CategoryBar
          setCategory={() => {}}
        />
      </div>

      {/* =================================================
          HOME SECTIONS
      ================================================= */}

      {showHomeSections && (
        <>
          <Banner />

          <PopularCategories />

          <div className="features-section">

            <div className="feature-box">
              <div className="feature-icon">
                📦
              </div>

              <h3>
                Vast Product Collection
              </h3>
            </div>

            <div className="feature-box">
              <div className="feature-icon">
                💲
              </div>

              <h3>
                Affordable Prices
              </h3>
            </div>

            <div className="feature-box">
              <div className="feature-icon">
                🚚
              </div>

              <h3>
                Fast Delivery
              </h3>
            </div>

            <div className="feature-box">
              <div className="feature-icon">
                ⭐
              </div>

              <h3>
                Excellent Customer Service
              </h3>
            </div>

          </div>
        </>
      )}

      {/* =================================================
          PRODUCTS
      ================================================= */}

      <div className="product-grid">

        {products.length === 0 && (
          <h2 className="no-products">
            No products found ❌
          </h2>
        )}

        {products.map((product) => {

          const imageUrl =
            getImageUrl(product.image);

          return (
            <div
              className="card"
              key={product.id}
            >

              {/* =================================================
                  PRODUCT CLICK AREA
              ================================================= */}

              <div
                className="product-click"
                onClick={() =>
                  navigate(
                    `/product/${product.id}`
                  )
                }
                style={{
                  cursor: "pointer"
                }}
              >

                {/* =================================================
                    PRODUCT IMAGE
                ================================================= */}

                <div className="image-container">

                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={
                        product.name ||
                        "Product"
                      }
                      className="product-img"

                      onLoad={() => {
                        console.log(
                          "IMAGE LOADED:",
                          imageUrl
                        );
                      }}

                      onError={(e) => {
                        console.log(
                          "IMAGE FAILED:",
                          imageUrl
                        );
                      }}
                    />
                  ) : (
                    <div className="no-image">
                      No Image
                    </div>
                  )}

                </div>

                {/* =================================================
                    PRODUCT NAME
                ================================================= */}

                <h3>
                  {product.name &&
                  product.name.length > 40
                    ? product.name.substring(
                        0,
                        40
                      ) + "..."
                    : product.name}
                </h3>

              </div>

              {/* =================================================
                  PRICE
              ================================================= */}

              <p>
                ${product.price}
              </p>

              {/* =================================================
                  ADD TO CART
              ================================================= */}

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();

                  addToCart(
                    product.id
                  );
                }}
              >
                Add to Cart
              </button>

              {/* =================================================
                  WISHLIST
              ================================================= */}

              <button
                type="button"
                className="wishlist-btn"
                onClick={(e) => {
                  e.stopPropagation();

                  addToWishlist(
                    product.id
                  );
                }}
              >
                ❤️ Wishlist
              </button>

            </div>
          );
        })}

      </div>

    </div>
  );
}
